import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import styles from "./Dropdown.module.css";

/** The height a menu needs below its trigger to open downward over the page: its tallest (18rem) and a little more. */
const MenuRoom = 300;

/**
 * One choice in a dropdown. A disabled option is shown but cannot be chosen. An option with a `group` sits under
 * that group's heading; consecutive options with the same heading share one.
 */
export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

/**
 * The portal's dropdown, used wherever a choice is picked from a list. It replaces the native select so every
 * dropdown looks and behaves the same. A trigger shows the current choice; the menu lists the options. It works
 * with the mouse and from the keyboard: Enter, Space or an arrow key opens it, the arrow keys move, Home and End
 * jump to the ends, Enter or Space chooses, and Escape or Tab closes it.
 *
 * @param props.id - The trigger's id, so a `<label htmlFor>` names it.
 * @param props.options - The choices, in display order.
 * @param props.value - The value of the chosen option. Matches no option when nothing is chosen.
 * @param props.onChange - Called with the value of the option the person chose.
 * @param props.labelledBy - The id of the element that names this dropdown, when it has no `<label>`.
 * @param props.ariaLabel - A name for this dropdown, when it has no visible label.
 * @param props.disabled - Whether the dropdown can be opened.
 * @param props.placement - Whether the menu opens below the trigger (the default) or above it, for a dropdown at the foot of a box.
 * @param props.size - `small` for a dropdown in a dense row, such as a table's pager.
 * @param props.shape - `pill` for a rounded trigger lit in the header's light, as a table pager's is.
 * @param props.align - `end` to line the menu up with the trigger's right edge, for a dropdown at the right of a box.
 * @param props.portal - Draws the menu over the page, outside the box the dropdown sits in, so a table or panel that clips or scrolls its
 * contents does not cut it off. It opens below the trigger, or above it when there is no room below.
 */
export function Dropdown({
  id,
  options,
  value,
  onChange,
  labelledBy,
  ariaLabel,
  disabled = false,
  placement = "below",
  size = "default",
  shape = "default",
  align = "start",
  portal = false,
}: {
  id: string;
  options: readonly DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placement?: "below" | "above";
  size?: "default" | "small";
  shape?: "default" | "pill";
  align?: "start" | "end";
  portal?: boolean;
  labelledBy?: string;
  ariaLabel?: string;
  disabled?: boolean;
}): React.JSX.Element {
  const idBase = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [labelId, setLabelId] = useState<string>();
  const [anchor, setAnchor] = useState<DOMRect | null>(null);

  // The menu takes its name from the same label as the trigger. A `<label htmlFor>` names the trigger by its id, so the
  // label is given an id of its own to point the menu at it.
  useEffect(() => {
    const label = document.querySelector<HTMLLabelElement>(`label[for="${CSS.escape(id)}"]`);
    if (label === null) {
      return;
    }

    label.id ||= `${idBase}-label`;
    setLabelId(label.id);
  }, [id, idBase]);

  const chosenIndex = options.findIndex((option) => option.value === value);
  const chosen = chosenIndex >= 0 ? options[chosenIndex] : undefined;

  // Focus moves to the menu when it opens, so the arrow keys work straight away. A menu over the page appears once it has been placed.
  const menuShown = open && (!portal || anchor !== null);
  useEffect(() => {
    if (menuShown) {
      menuRef.current?.focus();
    }
  }, [menuShown]);

  // A menu drawn over the page is placed from the trigger, and follows it as the page scrolls or resizes.
  useLayoutEffect(() => {
    if (!open || !portal) {
      return;
    }

    const measure = () => setAnchor(triggerRef.current?.getBoundingClientRect() ?? null);
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, portal]);

  // A click anywhere outside the dropdown closes the menu.
  useEffect(() => {
    if (!open) {
      return;
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node) && !menuRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [open]);

  const openMenu = () => {
    setActiveIndex(chosenIndex >= 0 ? chosenIndex : options.findIndex((option) => !option.disabled));
    setOpen(true);
  };

  const closeMenu = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  };

  const choose = (index: number) => {
    const option = options[index];
    if (option === undefined || option.disabled) {
      return;
    }

    onChange(option.value);
    closeMenu(true);
  };

  /** The index of the next enabled option from `from`, moving by `step` and wrapping round. */
  const step = (from: number, delta: number): number => {
    for (let offset = 1; offset <= options.length; offset++) {
      const index = (((from + delta * offset) % options.length) + options.length) % options.length;
      if (!options[index].disabled) {
        return index;
      }
    }
    return from;
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      openMenu();
    }
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((index) => step(index, 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((index) => step(index, -1));
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(options.findIndex((option) => !option.disabled));
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(options.map((option) => !option.disabled).lastIndexOf(true));
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        choose(activeIndex);
        break;
      case "Escape":
        event.preventDefault();
        closeMenu(true);
        break;
      case "Tab":
        closeMenu(false);
        break;
    }
  };

  const menuId = `${idBase}-menu`;

  // Over the page, the menu is fixed to the trigger's edge: below it, or above it when the room below is short of the menu's height.
  const flipped = anchor !== null && anchor.bottom + MenuRoom > window.innerHeight && anchor.top > MenuRoom;
  const portalStyle: React.CSSProperties | undefined =
    portal && anchor !== null
      ? { position: "fixed", left: anchor.left, minWidth: anchor.width, ...(flipped ? { bottom: window.innerHeight - anchor.top + 4, top: "auto" } : { top: anchor.bottom + 4 }) }
      : undefined;

  const menu = open ? (
        <ul
          ref={menuRef}
          id={menuId}
          role="listbox"
          tabIndex={-1}
          aria-labelledby={labelledBy ?? labelId}
          aria-label={labelledBy ?? labelId ? undefined : ariaLabel}
          aria-activedescendant={activeIndex >= 0 ? `${idBase}-option-${activeIndex}` : undefined}
          className={[styles.menu, placement === "above" ? styles.menuAbove : "", align === "end" ? styles.menuEnd : "", portal ? styles.menuPortal : ""].filter(Boolean).join(" ")}
          style={portalStyle}
          onKeyDown={onMenuKeyDown}
        >
          {options.map((option, index) => (
            <Fragment key={option.value}>
              {option.group !== undefined && (index === 0 || options[index - 1].group !== option.group) && (
                <li role="presentation" className={styles.group}>
                  {option.group}
                </li>
              )}
              <li
                id={`${idBase}-option-${index}`}
                role="option"
                aria-selected={index === chosenIndex}
                aria-disabled={option.disabled ?? false}
                className={`${styles.option} ${index === activeIndex ? styles.optionActive : ""} ${option.disabled ? styles.optionDisabled : ""}`}
                onMouseEnter={() => {
                  if (!option.disabled) {
                    setActiveIndex(index);
                  }
                }}
                onClick={() => choose(index)}
              >
                <span className={styles.optionLabel}>{option.label}</span>
                {index === chosenIndex && (
                  <svg className={styles.tick} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                    <path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </li>
            </Fragment>
          ))}
        </ul>
  ) : null;

  return (
    <div className={styles.dropdown} ref={rootRef}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={[styles.trigger, size === "small" ? styles.triggerSmall : "", shape === "pill" ? styles.triggerPill : ""].filter(Boolean).join(" ")}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : ariaLabel}
        onClick={() => (open ? closeMenu(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={styles.value}>{chosen?.label ?? ""}</span>
        <svg className={styles.chevron} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {portal ? (anchor !== null && menu !== null ? createPortal(menu, document.body) : null) : menu}
    </div>
  );
}
